import { type GrapeAPIErrorType } from 'components/hooks/GrapeAPIError'
import { useOnboarding } from 'components/member_onboarding_v2/OnboardingContext'
import { type StepComponentProps } from 'components/member_onboarding_v2/types'
import { SkeletonItem } from 'components/reusable/SkeletonItem'
import { Banner } from 'slab'
import {
  useGetMemberOnboardingEnrollmentEventsEnrollmentEventIdMedicalPlansDetails as useGetMedicalPlansDetails,
} from 'utils/web_api'

import { type MedicalPlanDisplayItem } from '../../medicalPlanTypes'
import { MedicalPlanDetailHeader } from '../MedicalPlanDetailHeader'
import { MedicalPlanDetailSummary } from '../MedicalPlanDetailSummary'
import { MedicalDetailPlanTable } from '../PlanTable/DetailPlanTable'

export const MedicalDetailsError = ({
  error,
}: {
  error?: GrapeAPIErrorType;
}) => {
  return (
    <Banner
      title='A problem has occurred'
      type='notice'
    >
      <p>
        We were unable to retrieve the details for this plan. Please try again later.
        <br />
        {error?.requestId && <b>Request ID: {error.requestId}</b>}
      </p>
    </Banner>
  )
}

export const MedicalDetails = ({
  medicalPlan,
  isIndividual,
  navigateToSpecificStep,
  onLaunchVerificationModal,
  showInNetworkVerificationCta,
}: {
  medicalPlan: MedicalPlanDisplayItem;
  isIndividual: boolean;
  navigateToSpecificStep: StepComponentProps['navigateToSpecificStep'];
  onLaunchVerificationModal?: () => void;
  showInNetworkVerificationCta?: boolean
}) => {
  const { enrollmentEventId } = useOnboarding()
  const planId = medicalPlan.plan.id

  const {
    data: plans,
    isLoading,
    error,
  } = useGetMedicalPlansDetails(
    enrollmentEventId,
    {
      'plan_ids[]': [planId],
    },
    {
      query: {
        retry: false,
        queryKey: [
          'enrollmentEvent',
          enrollmentEventId,
          'medical_plans',
          planId,
        ],
        enabled: true,
      },
    }
  )

  const planDisplayItem = plans?.data?.[0]

  return (
    <div className='flex flex-col'>
      <div className='sticky top-0 z-10 bg-white flex flex-row items-start gap-4 pb-4 w-full'>
        <MedicalPlanDetailHeader plan={medicalPlan.plan} iconOnly />
      </div>
      {isLoading && <SkeletonItem widthClass='w-full' heightClass='h-[calc(100vh-350px)]' />}
      {!!error && <MedicalDetailsError error={error as GrapeAPIErrorType} />}
      {planDisplayItem &&
        <>
          <MedicalPlanDetailSummary
            plan={planDisplayItem.plan}
            isIndividual={isIndividual}
            providerInNetworkMap={planDisplayItem.provider_in_network_map}
            inNetworkProvidersCount={planDisplayItem.in_network_providers_count}
            onLaunchVerificationModal={onLaunchVerificationModal}
            showInNetworkVerificationCta={showInNetworkVerificationCta}
            onSelectMorePlansForVerification={() => navigateToSpecificStep?.(`in_network_verification:${planDisplayItem.plan.id}`)}
          />
          <MedicalDetailPlanTable planDisplayItems={[planDisplayItem]} type='thatch' navigateToSpecificStep={navigateToSpecificStep} />
        </>}
    </div>
  )
}
